import arrowPopular from "@assets/popularArrow.svg";
import ButtonVariants from "@core-components/button/ButtonVariants";
import SSCarousel from "@core-components/carousel/Carousel";
import Emoji from "@core-components/emoji/Emoji";
import Icon from "@core-components/icon/Icon";
import SSHeadline from "@core-components/title/SSHeadline";
import SSWhiteText from "@core-components/title/components/SSWhiteText";
import EPublicRoutes from "@core-types/routes.enum";
import useAppLanguage from "@hooks/language/useAppLanguage";
import useAppTranslation from "@hooks/language/useAppTranslation";
import { Image, SimpleGrid, Stack, Text } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import SSDiscoverCard from "./DiscoverCard";

const SSDiscoverCarousel: React.FC = () => {
  const { locationUrlBuilder } = useAppLanguage();
  const { t } = useAppTranslation("pages.home.discoverCarousel");

  return (
    <div className="w-full md:space-y-[80px]">
      <SSHeadline
        header={
          <Text
            className="font-inter font-semibold text-gradient-blue-purple"
            fw={800}
            variant="gradient"
          >
            {t("title")}
          </Text>
        }
        subTitle={
          <Text className="font-inter font-normal text-primary-dark-text">
            {t("subtitle")}
          </Text>
        }
        props={{
          stack: {
            align: "left",
            className: "w-full pl-3 xs:pl-10",
          },
          title: {
            size: "h1",
          },
        }}
      >
        <SSWhiteText>{t("accountTypes")}</SSWhiteText>&nbsp;
        <Emoji.Balloon />
      </SSHeadline>

      <SSCarousel className="xl:hidden" slideSize={{ base: "auto" }}>
        <Stack align="center" className="relative">
          <div className="-translate-y-1/2 absolute z-[1000] flex w-full translate-x-3/4">
            <div className="relative">
              <Text
                className={
                  // base
                  "-top-2 absolute w-[160px] font-inter font-medium text-gradient-blue-purple text-lg " +
                  // sm and lower
                  "-left-36 " +
                  // above sm
                  "sm:left-14 sm:translate-x-0"
                }
              >
                {t("popular")}
              </Text>
            </div>
            <Image
              alt={t("altArrow")}
              className="-scale-x-100 sm:scale-x-100"
              fit="contain"
              h={50}
              loading="lazy"
              src={arrowPopular}
              title={t("altArrow")}
              w={"fit-content"}
            />
          </div>

          <SSDiscoverCard
            key={1}
            button={
              <ButtonVariants.Discover
                href={locationUrlBuilder(EPublicRoutes.SMURFS_NORMAL)}
              />
            }
            detailLinesProps={[
              {
                icon: IconCheck,
                color: "#00B67A",
                label: t("types.normal.features.affordable"),
              },
              {
                icon: IconCheck,
                color: "#00B67A",
                label: t("types.normal.features.be"),
              },
              {
                icon: IconCheck,
                color: "#00B67A",
                label: t("types.normal.features.instant"),
              },
              {
                icon: IconCheck,
                color: "#00B67A",
                label: t("types.normal.features.modifiable"),
              },
              {
                icon: IconCheck,
                color: "#00B67A",
                label: t("types.normal.features.unranked"),
              },

              {
                icon: IconCheck,
                color: "#00B67A",
                label: t("types.normal.features.warranty"),
              },
            ]}
            icon={<Icon.Website size={50} />}
            subtitle={t("types.normal.subtitle")}
            title={t("types.normal.title")}
          />
        </Stack>
        <SSDiscoverCard
          button={
            <ButtonVariants.Discover
              href={locationUrlBuilder(EPublicRoutes.SMURFS_HANDLEVEL)}
            />
          }
          detailLinesProps={[
            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.handlevel.features.noban"),
            },
            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.handlevel.features.be"),
            },
            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.handlevel.features.instant"),
            },
            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.handlevel.features.modifiable"),
            },
            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.handlevel.features.unranked"),
            },

            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.handlevel.features.warranty"),
            },
          ]}
          icon={<Icon.Shield size={50} />}
          subtitle={t("types.handlevel.subtitle")}
          title={t("types.handlevel.title")}
        />
        <SSDiscoverCard
          button={
            <ButtonVariants.Discover
              href={locationUrlBuilder(EPublicRoutes.SMURFS_SKIN)}
            />
          }
          detailLinesProps={[
            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.skin.features.otp"),
            },
            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.skin.features.be"),
            },
            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.skin.features.instant"),
            },
            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.skin.features.modifiable"),
            },
            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.skin.features.unranked"),
            },

            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.skin.features.warranty"),
            },
          ]}
          icon={<Icon.Profile size={50} />}
          subtitle={t("types.skin.subtitle")}
          title={t("types.skin.title")}
        />
        <SSDiscoverCard
          button={
            <ButtonVariants.Discover
              href={locationUrlBuilder(EPublicRoutes.SMURFS_RANKED)}
            />
          }
          detailLinesProps={[
            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.ranked.features.ranked"),
            },
            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.ranked.features.instant"),
            },
            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.ranked.features.modifiable"),
            },
            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.ranked.features.warranty"),
            },
          ]}
          icon={<Icon.TrophyBlue size={50} />}
          subtitle={t("types.ranked.subtitle")}
          title={t("types.ranked.title")}
        />
        ,
      </SSCarousel>
      <SimpleGrid
        className="mx-auto hidden items-stretch xl:grid"
        cols={4}
        w={"fit-content"}
      >
        <Stack align="center" className="relative">
          <div className="-translate-y-1/2 absolute z-[1000] flex w-full translate-x-3/4">
            <div className="relative">
              <Text
                className={
                  // base
                  "-top-2 absolute w-[160px] font-inter font-medium text-gradient-blue-purple text-lg " +
                  // sm and lower
                  "-left-36 " +
                  // above sm
                  "sm:left-14 sm:translate-x-0"
                }
              >
                {t("popular")}
              </Text>
            </div>
            <Image
              alt={t("altArrow")}
              className="-scale-x-100 sm:scale-x-100"
              fit="contain"
              h={50}
              loading="lazy"
              src={arrowPopular}
              title={t("altArrow")}
              w={"fit-content"}
            />
          </div>

          <SSDiscoverCard
            button={
              <ButtonVariants.Discover
                href={locationUrlBuilder(EPublicRoutes.SMURFS_NORMAL)}
              />
            }
            detailLinesProps={[
              {
                icon: IconCheck,
                color: "#00B67A",
                label: t("types.normal.features.affordable"),
              },
              {
                icon: IconCheck,
                color: "#00B67A",
                label: t("types.normal.features.be"),
              },
              {
                icon: IconCheck,
                color: "#00B67A",
                label: t("types.normal.features.instant"),
              },
              {
                icon: IconCheck,
                color: "#00B67A",
                label: t("types.normal.features.modifiable"),
              },
              {
                icon: IconCheck,
                color: "#00B67A",
                label: t("types.normal.features.unranked"),
              },

              {
                icon: IconCheck,
                color: "#00B67A",
                label: t("types.normal.features.warranty"),
              },
            ]}
            icon={<Icon.Website size={50} />}
            subtitle={t("types.normal.subtitle")}
            title={t("types.normal.title")}
          />
        </Stack>
        <SSDiscoverCard
          button={
            <ButtonVariants.Discover
              href={locationUrlBuilder(EPublicRoutes.SMURFS_HANDLEVEL)}
            />
          }
          detailLinesProps={[
            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.handlevel.features.noban"),
            },
            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.handlevel.features.be"),
            },
            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.handlevel.features.instant"),
            },
            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.handlevel.features.modifiable"),
            },
            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.handlevel.features.unranked"),
            },

            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.handlevel.features.warranty"),
            },
          ]}
          icon={<Icon.Shield size={50} />}
          subtitle={t("types.handlevel.subtitle")}
          title={t("types.handlevel.title")}
        />
        <SSDiscoverCard
          button={
            <ButtonVariants.Discover
              href={locationUrlBuilder(EPublicRoutes.SMURFS_SKIN)}
            />
          }
          detailLinesProps={[
            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.skin.features.otp"),
            },
            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.skin.features.be"),
            },
            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.skin.features.instant"),
            },
            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.skin.features.modifiable"),
            },
            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.skin.features.unranked"),
            },

            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.skin.features.warranty"),
            },
          ]}
          icon={<Icon.Profile size={50} />}
          subtitle={t("types.skin.subtitle")}
          title={t("types.skin.title")}
        />
        <SSDiscoverCard
          button={
            <ButtonVariants.Discover
              href={locationUrlBuilder(EPublicRoutes.SMURFS_RANKED)}
            />
          }
          detailLinesProps={[
            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.ranked.features.ranked"),
            },
            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.ranked.features.instant"),
            },
            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.ranked.features.modifiable"),
            },
            {
              icon: IconCheck,
              color: "#00B67A",
              label: t("types.ranked.features.warranty"),
            },
          ]}
          icon={<Icon.TrophyBlue size={50} />}
          subtitle={t("types.ranked.subtitle")}
          title={t("types.ranked.title")}
        />
      </SimpleGrid>
    </div>
  );
};

export default SSDiscoverCarousel;
